import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import WebsiteInterviewPage from '../../features/website-interview/components/WebsiteInterviewPage';
import { caseStudyAPI } from '../../features/case-study/api/casestudyAPI';

const readCachedCaseContext = (caseStudyId) => {
  if (!caseStudyId) return null;
  try {
    const raw = sessionStorage.getItem('pathbuddy_case_study_context');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.id && String(parsed.id) !== String(caseStudyId)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const WebsiteInterviewPageRoute = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const caseStudyId = params.get('caseStudyId');
  const assessment = params.get('assessment') || 'tutorial';
  const explicitToolType = params.get('toolType');
  const toolType = explicitToolType || (caseStudyId ? 'oxbridge_interview' : 'interview_simulator');

  const role = params.get('role') || '';
  const title = params.get('title') || undefined;

  const [caseStudyContext, setCaseStudyContext] = useState(() => {
    const cached = readCachedCaseContext(caseStudyId);
    if (!cached) return null;
    return { ...cached, assessment: cached.assessment || assessment };
  });

  useEffect(() => {
    if (!caseStudyId) {
      setCaseStudyContext(null);
      return;
    }

    let cancelled = false;
    const hydrateFromDb = async () => {
      try {
        const [caseStudy, figures] = await Promise.all([
          caseStudyAPI.getCaseStudyById(caseStudyId),
          caseStudyAPI.getCaseStudyFigures(caseStudyId).catch(() => []),
        ]);
        if (cancelled || !caseStudy) return;
        const cached = readCachedCaseContext(caseStudyId) || {};
        const next = {
          ...cached,
          id: String(caseStudy.id || caseStudyId),
          title: caseStudy.title || cached.title || '',
          subject: caseStudy.subject || cached.subject || '',
          industry: caseStudy.industry || cached.industry || '',
          description: caseStudy.description || cached.description || '',
          acts: caseStudy.acts || cached.acts || {},
          teaching_notes: caseStudy.teaching_notes || cached.teaching_notes || '',
          exhibits: Array.isArray(figures) && figures.length ? figures : (cached.exhibits || cached.figures || []),
          figures: Array.isArray(figures) && figures.length ? figures : (cached.figures || cached.exhibits || []),
          assessment: cached.assessment || assessment,
        };
        setCaseStudyContext(next);
        try {
          sessionStorage.setItem('pathbuddy_case_study_context', JSON.stringify(next));
        } catch {
          /* ignore quota */
        }
      } catch (err) {
        console.warn('Failed to hydrate case interview context from DB:', err);
        if (!cancelled) {
          const cached = readCachedCaseContext(caseStudyId);
          setCaseStudyContext(
            cached
              ? { ...cached, assessment: cached.assessment || assessment }
              : { id: String(caseStudyId), assessment, acts: {}, teaching_notes: '' }
          );
        }
      }
    };

    hydrateFromDb();
    return () => {
      cancelled = true;
    };
  }, [caseStudyId, assessment]);

  const appContext = useMemo(
    () =>
      toolType
        ? {
            title: title || (caseStudyId ? 'Case Tutorial' : 'Interview Simulator'),
            exam_name: toolType,
          }
        : undefined,
    [toolType, title, caseStudyId],
  );

  return (
    <ProtectedRoute requiresAuth={true}>
      {caseStudyId && !caseStudyContext ? (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading case tutorial...
        </div>
      ) : (
        <WebsiteInterviewPage
          appContext={appContext}
          initialRole={role}
          toolType={toolType}
          caseStudyContext={caseStudyId ? caseStudyContext : undefined}
        />
      )}
    </ProtectedRoute>
  );
};

export default WebsiteInterviewPageRoute;
